import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import clsx from "clsx";

import {
  Paper,
  makeStyles,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { i18n } from "../../translate/i18n";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInputCustom/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { SocketContext } from "../../context/Socket/SocketContext";
import useSettings from "../../hooks/useSettings";
// Voice providers + the in-call modal now live at the app-shell level
// (LoggedInLayout) so a call survives ticket switching. The banner + outcome
// panel stay here (ticket-bound) and consume the hoisted context.
import SoftphoneErrorBanner from "../SoftphoneErrorBanner";
import CallOutcomePanel from "../CallOutcomePanel";

// Must match ContactDrawer's own `drawerWidth` constant
// (frontend/src/components/ContactDrawer/index.js:30 — currently 340).
// The two used to drift: Ticket=320 vs ContactDrawer=340 left a 20px
// uncompensated gap on the chat's leading edge because mainWrapper's
// negative margin (drawerWidth here) was 20px short of the slot the
// drawer reserves in the flex row, producing a thin white strip the
// user could see on the trailing edge of the chat. Keep these in sync.
const drawerWidth = 340;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    // Desktop "persistent drawer slot" trick: ContactDrawer reserves
    // drawerWidth in the flex row even when closed, so the chat panel
    // uses a negative inline-margin to slide INTO that reserved space
    // visually. mainWrapperShift removes the offset when the drawer
    // opens, animating the chat back to its own column.
    marginRight: theme.direction !== "rtl" ? -drawerWidth : 0,
    marginLeft: theme.direction === "rtl" ? -drawerWidth : 0,
    // Phones use a separate fix: the drawer's outer width collapses
    // to 0 when closed (see ContactDrawer xs override), so it claims
    // no flex slot. The negative margin therefore has nothing to
    // absorb on mobile — it just shifts the chat panel off-screen
    // by drawerWidth, which is exactly the "bubbles overflow the
    // viewport edge" symptom Raviv showed. Zero the margin under sm.
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
      marginLeft: 0,
    },
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
  },

  // Leading cluster of the header row: mobile back-chevron + contact info.
  // On md+ the chevron isn't rendered, so this is a transparent flex
  // wrapper that preserves the existing layout (TicketInfo's own
  // marginLeft:auto keeps working against the flex:1 free space).
  headerLeading: {
    display: "flex",
    alignItems: "center",
    // flex 0 0 auto: leading cluster (avatar + name + #ticket) takes only its
    // content width and never shrinks, so the tags dropdown + action buttons
    // stay on the SAME row beside it instead of wrapping/cramping under the name
    // on a narrow chat panel (re-applied header fix).
    flex: "0 0 auto",
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      // Mirror TicketInfo's xs contact-strip (bg + divider) so the
      // chevron sits visually inside the same card row instead of on
      // the bare header background; the inner CardHeader drops its own
      // duplicate strip styling to avoid a doubled divider line.
      backgroundColor: theme.palette.type === "dark" ? "#131614" : "#f8fafc",
      borderBottom: `1px solid ${theme.palette.divider}`,
      "& .MuiCardHeader-root": {
        backgroundColor: "transparent",
        borderBottom: "none",
      },
    },
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  // Mobile-only back chevron: below the md breakpoint there is no
  // side-by-side ticket list, so the header needs an explicit way back.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [showTabGroups, setShowTabGroups] = useState(false);
  const { getSetting } = useSettings();
  const [showSelectMessageCheckbox, setShowSelectMessageCheckbox] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [forwardMessageModalOpen, setForwardMessageModalOpen] = useState(false);
  const socketManager = useContext(SocketContext);
  // Tracks the ticketId the route currently points at, so an in-flight
  // fetch for a previous ticket can detect it lost the race (latest-wins
  // guard in the fetch below) instead of painting stale data over the
  // newer ticket.
  const currentTicketIdRef = useRef(ticketId);
  // False until the first successful ticket load — the initial open shows
  // the header skeleton, but later switches keep the previous ticket
  // visible until the new one resolves (no blank-out on switch).
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    Promise.all([
      getSetting("CheckMsgIsGroup"),
      getSetting("groupsTab")
    ]).then(([ignoreGroups, groupsTab]) => {
      setShowTabGroups(ignoreGroups === "disabled" && groupsTab === "enabled");
    });
  }, []);

  useEffect(() => {
    currentTicketIdRef.current = ticketId;
    // Keep the PREVIOUS ticket/contact mounted while the new one loads —
    // wiping them here blanked the chat + header on every switch. The
    // state swaps atomically when the new data lands below; only the very
    // first open (nothing loaded yet) shows the header skeleton.
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    // 0ms delay: the setTimeout+clearTimeout shape is kept purely for
    // rapid-switch cancellation (switching again before the tick cancels
    // the queued fetch) — the old artificial 500ms "debounce" made every
    // chat switch feel sluggish for no reason.
    const requestedTicketId = ticketId;
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/u/" + requestedTicketId);
          // Latest-wins: if the route moved on while this request was in
          // flight, drop the response — otherwise ticket A's slow response
          // could paint over ticket B after a fast A→B switch.
          if (currentTicketIdRef.current !== requestedTicketId) return;
          const { queueId } = data;
          const { queues, profile } = user;

          setContact(data.contact);
          setTicket(data);
          hasLoadedOnceRef.current = true;
          setLoading(false);
        } catch (err) {
          if (currentTicketIdRef.current !== requestedTicketId) return;
          // Fail-to-inert: the keep-previous-ticket-visible optimization
          // above is only safe while the fetch SUCCEEDS. On failure the
          // route already points at the new uuid but `ticket` still holds
          // the previous customer — leaving it mounted would let the agent
          // read/act on the wrong chat. Restore the pre-optimization
          // invariant (blank inert state) on failure only.
          setTicket({});
          setContact({});
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 0);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");

    if (!ticketId) return;

    const socket = socketManager.GetSocket(companyId);

    const onConnectTicket = () => {
      socket.emit("joinChatBox", `${ticketId}`);
    }

    // Join chatbox immediately
    socket.emit("joinChatBox", `${ticketId}`);
    
    socketManager.onConnect(onConnectTicket);

    const onCompanyTicket = (data) => {
      // Compare with uuid from URL since ticket.id might not be loaded yet
      if (data.action === "update" && data.ticket.uuid === ticketId) {
        setTicket(data.ticket);
      }

      if (data.action === "delete" && data.ticketId === ticketId) {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    };

    const onCompanyContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    socket.on(`company-${companyId}-ticket`, onCompanyTicket);
    socket.on(`company-${companyId}-contact`, onCompanyContact);

    return () => {
      // Only remove listeners, DON'T disconnect socket!
      socket.off(`company-${companyId}-ticket`, onCompanyTicket);
      socket.off(`company-${companyId}-contact`, onCompanyContact);
      socket.emit("leaveChatBox", `${ticketId}`);
    };
  }, [ticketId, history, socketManager]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const renderTicketInfo = () => {
    if (ticket.id) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
        />
      );
    }
  };

  const renderMessagesList = () => {
    return (
      <>
        {/* Forward-selection bar (Raviv 2026-06-08): when the agent
            entered "select messages to forward" mode, surface a tiny
            sticky bar above the chat with the selected count, a Cancel
            button (otherwise selection mode was unescapable except by
            sending) and a Forward button that opens the picker. */}
        {showSelectMessageCheckbox && (
          <div
            style={{
              background: '#f0f0f0',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{selectedMessages.length} {i18n.t("messagesList.header.selected") || 'נבחרו'}</span>
            <Button
              size="small"
              onClick={() => {
                setShowSelectMessageCheckbox(false);
                setSelectedMessages([]);
              }}
            >
              {i18n.t("buttons.cancel") || 'ביטול'}
            </Button>
            <Button
              size="small"
              color="primary"
              disabled={selectedMessages.length === 0}
              onClick={() => setForwardMessageModalOpen(true)}
            >
              {i18n.t("messageOptionsMenu.forward") || 'העבר'}
            </Button>
          </div>
        )}
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
          markAsRead={true}
          showSelectMessageCheckbox={showSelectMessageCheckbox}
          setShowSelectMessageCheckbox={setShowSelectMessageCheckbox}
          setSelectedMessagesList={setSelectedMessages}
          selectedMessagesList={selectedMessages}
          forwardMessageModalOpen={forwardMessageModalOpen}
          setForwardMessageModalOpen={setForwardMessageModalOpen}

        ></MessagesList>
        <MessageInput ticket={ticket} showTabGroups />
      </>
    );
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.headerLeading}>
            {isMobile && (
              <IconButton
                aria-label="back"
                size="small"
                onClick={() => history.push("/tickets")}
              >
                {theme.direction === "rtl" ? (
                  <ArrowForwardIosIcon fontSize="small" />
                ) : (
                  <ArrowBackIosIcon fontSize="small" />
                )}
              </IconButton>
            )}
            {renderTicketInfo()}
          </div>
          <TicketActionButtons
          showSelectMessageCheckbox={showSelectMessageCheckbox}
          selectedMessages={selectedMessages}
          forwardMessageModalOpen={forwardMessageModalOpen}
          setForwardMessageModalOpen={setForwardMessageModalOpen}
          ticket={ticket}
          showTabGroups={showTabGroups}
          onTicketUpdate={setTicket} />
        </TicketHeader>
        {/* The in-call modal is now session-level (LoggedInLayout). These two
            stay ticket-bound and read the hoisted Voice context. */}
        <SoftphoneErrorBanner />
        <CallOutcomePanel ticket={ticket} />
        {/* <Paper>
          <TagsContainer ticket={ticket} />
        </Paper> */}
        <ReplyMessageProvider>
          <EditMessageProvider>
	        {renderMessagesList()}
          </EditMessageProvider>
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />
    </div>
  );
};

export default Ticket;
